// @ts-nocheck
/**
 * Shared list of comments left under a contractor's internal rating.
 *
 * Rendered in two places with the same data: the rating modal (light dialog,
 * comments can be removed) and the contact card that opens on hover (dark
 * tooltip, read-only). They must not drift — the client's ask was to see the
 * note AND its author right where the stars are.
 *
 * The API returns `notes` newest first; nothing here re-sorts it.
 */
import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { getUniversalDateWithTimeFormat } from "../../utils/methods/universalDateTimeFormat";
import { USER_ROLES } from "../../staticData/userRoles";
import { isUserRole } from "../../utils/methods/storeFunc";

/** "Sarah Ledford (Coordinator) · Wed 08/13/2026 at 09:12 am (EDT)" */
export const formatNoteByline = (note) => {
  const name = note?.createdBy?.name?.trim();
  const role = note?.createdBy?.userRole?.trim();
  const when = note?.createdAt
    ? getUniversalDateWithTimeFormat({ dateTime: note.createdAt })
    : null;

  // Legacy rows carry no author (the single-note field never recorded one).
  const who = name ? (role ? `${name} (${role})` : name) : "Unknown user";
  return when ? `${who} · ${when}` : who;
};

const ContractorRatingNotes = ({
  notes = [],
  allowDelete = false,
  onDelete = () => {},
  deleting = false,
  variant = "modal",
  emptyLabel = "No notes yet.",
  maxHeight = null,
}) => {
  const loggedInUser = useSelector((state) => state?.auth);
  const isSuperAdmin = isUserRole(USER_ROLES.SUPERADMIN);
  const isCard = variant === "card";

  const list = Array.isArray(notes) ? notes : [];

  if (!list.length) {
    return emptyLabel ? (
      <Typography
        sx={{
          fontSize: isCard ? "0.75rem" : "0.8rem",
          opacity: 0.55,
          fontStyle: "italic",
        }}
      >
        {emptyLabel}
      </Typography>
    ) : null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: isCard ? "0.35rem" : "0.6rem",
        width: "100%",
        // The card shows every comment in full — capping the block instead of
        // the text keeps a long history from pushing the popup off screen.
        ...(maxHeight
          ? { maxHeight, overflowY: "auto", pr: "0.25rem" }
          : {}),
      }}
    >
      {list.map((note, index) => {
        const noteId = note?._id ? String(note._id) : null;
        // No id means the legacy single-note field: read-only by design.
        const isMine =
          Boolean(loggedInUser?.userId) &&
          String(note?.createdBy?.userId ?? "") ===
            String(loggedInUser?.userId);
        const canDelete =
          allowDelete && Boolean(noteId) && (isMine || isSuperAdmin);

        return (
          <Box
            key={noteId ?? `legacy-${index}`}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "0.25rem",
              borderLeft: "2px solid rgba(255,255,255,0.25)",
              pl: "0.5rem",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: isCard ? "0.8rem" : "0.9rem",
                  lineHeight: 1.35,
                  // Comments are free text: break long words instead of
                  // widening the tooltip past its maxWidth.
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {note?.text}
              </Typography>
              <Typography
                sx={{
                  fontSize: isCard ? "0.65rem" : "0.72rem",
                  opacity: 0.6,
                  mt: "0.1rem",
                }}
              >
                {formatNoteByline(note)}
              </Typography>
            </Box>

            {canDelete && (
              <Tooltip title="Remove this note">
                <span>
                  <IconButton
                    size="small"
                    disabled={deleting}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDelete(noteId);
                    }}
                    // Colour is explicit because the MUI palette is the default
                    // LIGHT one: an icon that inherits gets `rgba(0,0,0,0.54)`
                    // and disappears on both dark surfaces this renders on —
                    // the button was there, just invisible.
                    sx={{
                      p: "2px",
                      color: "rgba(255,255,255,0.7)",
                      "&:hover": { color: "#fff" },
                      "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

ContractorRatingNotes.propTypes = {
  notes: PropTypes.array,
  allowDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  deleting: PropTypes.bool,
  variant: PropTypes.oneOf(["modal", "card"]),
  emptyLabel: PropTypes.string,
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ContractorRatingNotes;
